import { useEffect, useRef, useState } from 'react';
import { Film } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VideoThumbnailProps {
  /** Video source URL */
  src: string;
  /** Optional poster/thumbnail URL to use instead of generating a frame */
  poster?: string | null;
  className?: string;
  /** Seconds into the video to capture the frame (default: 1s) */
  captureTime?: number;
  /** Size of the play badge: 'sm' | 'md' | 'lg' */
  badgeSize?: 'sm' | 'md' | 'lg';
  /** When true, silently autoplays a muted preview on hover (desktop) */
  hoverPreview?: boolean;
}

/**
 * Displays a thumbnail frame for a video.
 *
 * If a `poster` URL is provided it is used directly. Otherwise the component
 * loads the video (muted, metadata only), seeks to `captureTime`, and draws
 * the frame onto a canvas to produce a data-URL thumbnail. This happens
 * entirely client-side — no server needed.
 *
 * With `hoverPreview`, hovering the card plays a muted, looping inline preview
 * for a lively social-media feel; it pauses and resets on mouse-out.
 *
 * Falls back to a dark placeholder with a film icon if frame capture fails
 * (e.g. the host doesn't send CORS headers that allow canvas readback).
 */
export function VideoThumbnail({
  src,
  poster,
  className,
  captureTime = 1,
  badgeSize = 'md',
  hoverPreview = false,
}: VideoThumbnailProps) {
  const [frame, setFrame] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    // If we have a poster, no need to generate a frame.
    if (poster) return;

    let cancelled = false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = src;

    const cleanup = () => {
      video.removeAttribute('src');
      video.load();
    };

    const captureFrame = () => {
      if (cancelled) return;
      try {
        const canvas = document.createElement('canvas');
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (!w || !h) {
          setFailed(true);
          cleanup();
          return;
        }
        const scale = Math.min(1, 720 / w);
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setFailed(true);
          cleanup();
          return;
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
        if (!cancelled) setFrame(dataUrl);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        cleanup();
      }
    };

    const onLoadedMetadata = () => {
      const target = Math.min(captureTime, (video.duration || captureTime) / 2);
      try {
        video.currentTime = target || 0;
      } catch {
        captureFrame();
      }
    };

    const onSeeked = () => captureFrame();
    const onError = () => {
      if (!cancelled) setFailed(true);
      cleanup();
    };

    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', onSeeked);
    video.addEventListener('error', onError);

    return () => {
      cancelled = true;
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('seeked', onSeeked);
      video.removeEventListener('error', onError);
      cleanup();
    };
  }, [src, poster, captureTime]);

  const badgeClasses = {
    sm: 'p-1.5',
    md: 'p-3',
    lg: 'p-4',
  }[badgeSize];

  const iconClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  }[badgeSize];

  const thumbSrc = poster || frame;

  const handleEnter = () => {
    if (!hoverPreview) return;
    setPreviewing(true);
    const v = previewRef.current;
    if (v) {
      v.currentTime = 0;
      v.play().catch(() => setPreviewing(false));
    }
  };

  const handleLeave = () => {
    if (!hoverPreview) return;
    setPreviewing(false);
    const v = previewRef.current;
    if (v) {
      v.pause();
    }
  };

  return (
    <div
      className={cn('relative bg-neutral-900 overflow-hidden', className)}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {thumbSrc ? (
        <img
          src={thumbSrc}
          alt=""
          className={cn(
            'w-full h-full object-cover transition-opacity duration-300',
            previewing ? 'opacity-0' : 'opacity-100',
          )}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-neutral-800 to-neutral-900 aspect-video">
          {failed && <Film className="w-10 h-10 text-neutral-600" />}
        </div>
      )}

      {/* Hover preview video (desktop only, lazy-mounted) */}
      {hoverPreview && (
        <video
          ref={previewRef}
          src={src}
          muted
          loop
          playsInline
          preload="none"
          className={cn(
            'absolute inset-0 w-full h-full object-cover transition-opacity duration-300',
            previewing ? 'opacity-100' : 'opacity-0 pointer-events-none',
          )}
        />
      )}

      {/* Play badge overlay — hidden while previewing */}
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-200',
          previewing ? 'opacity-0' : 'opacity-100',
        )}
      >
        <span className={cn('bg-black/55 rounded-full backdrop-blur-sm', badgeClasses)}>
          <PlayIcon className={cn('text-white', iconClasses)} />
        </span>
      </span>
    </div>
  );
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
